import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Store, XCircle, Edit, CheckSquare, Trash2, Check, Image as ImageIcon, Upload, Plus, Search, Download, FileSpreadsheet, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { getBackendUrl } from '../../utils/api';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

export const MasterGalleryContent = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  
  // Estados para upload de imagem
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);
  
  // Estados para Import/Export CSV
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [exporting, setExporting] = useState(false);
  const csvInputRef = React.useRef(null);
  
  // Estados para nova categoria
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [newCategory, setNewCategory] = useState('');

  // ====== ESTADOS PARA EXCLUSÃO EM MASSA ======
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedImageIds, setSelectedImageIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // ====== ESTADOS PARA IMPORTAR DE LOJAS ======
  const [activeTab, setActiveTab] = useState('gallery'); // 'gallery' ou 'import'
  const [productsWithImages, setProductsWithImages] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [restaurantsList, setRestaurantsList] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const [importFilters, setImportFilters] = useState({
    restaurant_id: '',
    category: '',
    search: ''
  });
  const [importPage, setImportPage] = useState(1);
  const [importTotalPages, setImportTotalPages] = useState(1);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [importingProducts, setImportingProducts] = useState(false);
  const [showImportProductModal, setShowImportProductModal] = useState(false);
  const [productToImport, setProductToImport] = useState(null);
  const [importFormData, setImportFormData] = useState({ name: '', keywords: '', category: '' });
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    image_url: ''
  });

  // Categorias predefinidas
  const predefinedCategories = [
    'hamburgueres',
    'pizzas',
    'acai',
    'bebidas',
    'sobremesas',
    'porcoes',
    'marmitas',
    'lanches',
    'salgados',
    'doces',
    'outros'
  ];

  const fetchImages = async () => {
    setLoading(true);
    try {
      let url = `${API}/master/gallery`;
      const params = new URLSearchParams();
      if (filterCategory) params.append('category', filterCategory);
      if (searchTerm) params.append('search', searchTerm);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url);
      setImages(response.data);
      
      // Extrair categorias únicas
      const uniqueCategories = [...new Set(response.data.map(img => img.category))];
      setCategories(uniqueCategories);
    } catch (error) {
      console.error('Erro ao carregar galeria:', error);
      toast.error('Erro ao carregar galeria');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImages();
  }, [filterCategory, searchTerm]);

  // ====== FUNÇÕES PARA IMPORTAR DE LOJAS ======
  
  // Buscar lista de restaurantes para filtro
  const fetchRestaurantsList = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master/gallery/restaurants-list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRestaurantsList(response.data.restaurants || []);
    } catch (error) {
      console.error('Erro ao carregar restaurantes:', error);
    }
  };

  // Buscar categorias de produtos para filtro
  const fetchProductCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master/gallery/product-categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProductCategories(response.data.categories || []);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  // Buscar produtos com imagem
  const fetchProductsWithImages = async (page = 1) => {
    setLoadingProducts(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 20);
      if (importFilters.restaurant_id) params.append('restaurant_id', importFilters.restaurant_id);
      if (importFilters.category) params.append('category', importFilters.category);
      if (importFilters.search) params.append('search', importFilters.search);
      
      const response = await axios.get(`${API}/master/gallery/products-with-images?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setProductsWithImages(response.data.products || []);
      setImportTotalPages(response.data.total_pages || 1);
      setImportPage(page);
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
      toast.error('Erro ao carregar produtos das lojas');
    } finally {
      setLoadingProducts(false);
    }
  };

  // Carregar dados quando mudar para aba de importação
  useEffect(() => {
    if (activeTab === 'import') {
      fetchRestaurantsList();
      fetchProductCategories();
      fetchProductsWithImages(1);
    }
  }, [activeTab]);

  // Recarregar produtos quando filtros mudam
  useEffect(() => {
    if (activeTab === 'import') {
      fetchProductsWithImages(1);
    }
  }, [importFilters]);

  // Gerar palavras-chave automáticas baseado no nome e categoria
  const generateKeywords = (name, category, description = '') => {
    const keywords = [];
    // Adicionar nome em minúsculas
    if (name) {
      keywords.push(name.toLowerCase());
      // Adicionar palavras individuais do nome (mais de 2 caracteres)
      name.toLowerCase().split(/[\s\-\_\,\.]+/).forEach(word => {
        if (word.length > 2 && !keywords.includes(word)) {
          keywords.push(word);
        }
      });
    }
    // Adicionar categoria
    if (category) {
      const catLower = category.toLowerCase();
      if (!keywords.includes(catLower)) {
        keywords.push(catLower);
      }
    }
    // Extrair palavras da descrição (se disponível)
    if (description) {
      const descWords = description.toLowerCase().split(/[\s\-\_\,\.]+/);
      descWords.forEach(word => {
        if (word.length > 3 && !keywords.includes(word) && keywords.length < 15) {
          keywords.push(word);
        }
      });
    }
    // Adicionar variações comuns de produtos alimentícios
    const variations = {
      'hamburguer': ['burger', 'lanche', 'sanduiche'],
      'x-': ['cheese', 'queijo', 'lanche'],
      'pizza': ['massa', 'italiana'],
      'açaí': ['acai', 'fruta', 'gelado'],
      'suco': ['bebida', 'fruta', 'natural'],
      'refri': ['refrigerante', 'bebida', 'coca'],
      'batata': ['frita', 'fritas', 'acompanhamento'],
      'salada': ['verde', 'fit', 'saudavel'],
      'combo': ['promocao', 'kit', 'oferta']
    };
    const nameLower = name?.toLowerCase() || '';
    Object.entries(variations).forEach(([key, values]) => {
      if (nameLower.includes(key)) {
        values.forEach(v => {
          if (!keywords.includes(v)) keywords.push(v);
        });
      }
    });
    return keywords.slice(0, 20).join(', ');
  };

  // Abrir modal de importação individual
  const openImportProductModal = (product) => {
    setProductToImport(product);
    setImportFormData({
      name: product.name,
      keywords: generateKeywords(product.name, product.category, product.description),
      category: product.category || 'Geral'
    });
    setShowImportProductModal(true);
  };

  // Importar produto individual
  const handleImportProduct = async () => {
    if (!productToImport) return;
    
    setImportingProducts(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/master/gallery/import-from-product`, {
        product_id: productToImport.id,
        name: importFormData.name,
        keywords: importFormData.keywords,
        category: importFormData.category
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success('Imagem importada para galeria!');
        setShowImportProductModal(false);
        setProductToImport(null);
        // Atualizar lista para marcar como importado
        fetchProductsWithImages(importPage);
        // Atualizar galeria também
        fetchImages();
      }
    } catch (error) {
      console.error('Erro ao importar:', error);
      toast.error(error.response?.data?.detail || 'Erro ao importar imagem');
    } finally {
      setImportingProducts(false);
    }
  };

  // Toggle seleção de produto para importação em lote
  const toggleProductSelection = (product) => {
    setSelectedProducts(prev => {
      const exists = prev.find(p => p.id === product.id);
      if (exists) {
        return prev.filter(p => p.id !== product.id);
      } else {
        return [...prev, {
          product_id: product.id,
          name: product.name,
          keywords: generateKeywords(product.name, product.category, product.description),
          category: product.category || 'Geral'
        }];
      }
    });
  };

  // Importar múltiplos produtos selecionados
  const handleImportSelected = async () => {
    if (selectedProducts.length === 0) {
      toast.error('Selecione pelo menos um produto');
      return;
    }
    
    setImportingProducts(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/master/gallery/import-multiple-products`, 
        selectedProducts,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(`${response.data.imported} imagens importadas!`);
        if (response.data.skipped > 0) {
          toast.info(`${response.data.skipped} já existiam na galeria`);
        }
        setSelectedProducts([]);
        fetchProductsWithImages(importPage);
        fetchImages();
      }
    } catch (error) {
      console.error('Erro ao importar:', error);
      toast.error('Erro ao importar imagens');
    } finally {
      setImportingProducts(false);
    }
  };

  // Função para lidar com seleção de arquivo
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tipo
      const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
      if (!validTypes.includes(file.type)) {
        toast.error('Formato não suportado. Use JPG, PNG ou WEBP');
        return;
      }
      // Validar tamanho (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Arquivo muito grande. Máximo: 5MB');
        return;
      }
      setSelectedFile(file);
      // Criar preview
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  // Função para fazer upload
  const handleUpload = async () => {
    if (!selectedFile) return;
    
    setUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', selectedFile);
      
      const response = await axios.post(`${API}/master/gallery/upload`, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        setFormData(prev => ({ ...prev, image_url: response.data.image_url }));
        setSelectedFile(null);
        setImagePreview(null);
        toast.success('Upload realizado!');
      }
    } catch (error) {
      console.error('Erro no upload:', error);
      toast.error('Erro ao fazer upload: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
    }
  };

  // Limpar preview
  const clearImagePreview = () => {
    setSelectedFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingImage) {
        await axios.put(`${API}/master/gallery/${editingImage.id}`, formData);
        toast.success('Imagem atualizada!');
      } else {
        await axios.post(`${API}/master/gallery`, formData);
        toast.success('Imagem adicionada!');
      }
      setShowAddModal(false);
      setShowEditModal(false);
      setFormData({ name: '', description: '', category: '', image_url: '' });
      setEditingImage(null);
      clearImagePreview();
      fetchImages();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error('Erro ao salvar imagem');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta imagem?')) return;
    
    try {
      await axios.delete(`${API}/master/gallery/${imageId}`);
      toast.success('Imagem excluída!');
      fetchImages();
    } catch (error) {
      console.error('Erro ao excluir:', error);
      toast.error('Erro ao excluir imagem');
    }
  };

  // ====== HANDLERS EXCLUSÃO EM MASSA ======
  const toggleSelectionMode = () => {
    setSelectionMode(prev => {
      if (prev) setSelectedImageIds([]);
      return !prev;
    });
  };

  const toggleSelectImage = (imageId) => {
    setSelectedImageIds(prev =>
      prev.includes(imageId) ? prev.filter(id => id !== imageId) : [...prev, imageId]
    );
  };

  const selectAllImages = () => {
    setSelectedImageIds(images.map(img => img.id));
  };

  const clearSelection = () => {
    setSelectedImageIds([]);
  };

  const handleBulkDelete = async () => {
    if (selectedImageIds.length === 0) return;
    if (!window.confirm(`Excluir ${selectedImageIds.length} imagem(ns) da galeria? Esta ação não pode ser desfeita.`)) return;

    setBulkDeleting(true);
    try {
      const response = await axios.post(`${API}/master/gallery/bulk-delete`, {
        image_ids: selectedImageIds
      });
      const deleted = response.data?.deleted_count ?? selectedImageIds.length;
      toast.success(`${deleted} imagem(ns) excluída(s)!`);
      setSelectedImageIds([]);
      setSelectionMode(false);
      fetchImages();
    } catch (error) {
      console.error('Erro ao excluir em massa:', error);
      toast.error(error.response?.data?.detail || 'Erro ao excluir imagens');
    } finally {
      setBulkDeleting(false);
    }
  };

  const openEditModal = (image) => {
    setEditingImage(image);
    setFormData({
      name: image.name,
      description: image.description,
      category: image.category,
      image_url: image.image_url
    });
    setShowEditModal(true);
  };

  // Exportar CSV
  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master/gallery/export-csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Criar link de download
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'galeria_imagens.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('CSV exportado com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar:', error);
      toast.error('Erro ao exportar CSV');
    } finally {
      setExporting(false);
    }
  };

  // Importar CSV
  const handleImportCSV = async () => {
    if (!importFile) {
      toast.error('Selecione um arquivo CSV');
      return;
    }
    
    setImporting(true);
    setImportResult(null);
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', importFile);
      
      const response = await axios.post(`${API}/master/gallery/import-csv`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setImportResult(response.data);
      
      if (response.data.imported > 0) {
        toast.success(`${response.data.imported} imagens importadas!`);
        fetchImages();
      } else {
        toast.warning('Nenhuma imagem foi importada');
      }
    } catch (error) {
      console.error('Erro ao importar:', error);
      toast.error(error.response?.data?.detail || 'Erro ao importar CSV');
    } finally {
      setImporting(false);
    }
  };

  // Download template CSV
  const downloadTemplate = () => {
    const template = `nome,categoria,palavras_chave,url_cloudinary
X-Bacon Artesanal,hamburgueres,"hamburguer bacon cheddar artesanal",https://res.cloudinary.com/exemplo/image/upload/v123/xbacon.jpg
Pizza Margherita,pizzas,"pizza margherita tomate manjericao mussarela",https://res.cloudinary.com/exemplo/image/upload/v123/margherita.jpg
Açaí com Morango,acai,"acai morango granola banana",https://res.cloudinary.com/exemplo/image/upload/v123/acai.jpg

INSTRUÇÕES:
- nome: Nome da imagem (obrigatório)
- categoria: hamburgueres, pizzas, acai, bebidas, sobremesas, porcoes, marmitas, lanches, salgados, doces, outros
- palavras_chave: Separe por espaços ou vírgulas (usado para vincular aos produtos)
- url_cloudinary: URL completa da imagem no Cloudinary (OBRIGATÓRIO - apenas URLs do Cloudinary são aceitas)`;
    
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'template_galeria.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <div className="space-y-6 pb-8" style={{ minHeight: '100%', WebkitOverflowScrolling: 'touch' }}>
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Galeria de Imagens</h1>
          <p className="text-gray-400 mt-1">Gerencie imagens para os lojistas utilizarem</p>
        </div>
        {activeTab === 'gallery' && (
          <div className="flex flex-wrap gap-2">
            {!selectionMode ? (
              <button
                onClick={toggleSelectionMode}
                disabled={images.length === 0}
                className="flex items-center gap-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50"
                data-testid="toggle-selection-btn"
              >
                <CheckSquare size={16} />
                <span className="hidden sm:inline">Selecionar</span>
              </button>
            ) : (
              <>
                <button
                  onClick={selectAllImages}
                  className="flex items-center gap-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                  data-testid="select-all-btn"
                >
                  <CheckSquare size={16} />
                  <span className="hidden sm:inline">Selecionar todos</span>
                </button>
                <button
                  onClick={clearSelection}
                  disabled={selectedImageIds.length === 0}
                  className="flex items-center gap-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50"
                  data-testid="clear-selection-btn"
                >
                  Limpar
                </button>
                <button
                  onClick={handleBulkDelete}
                  disabled={selectedImageIds.length === 0 || bulkDeleting}
                  className="flex items-center gap-2 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
                  data-testid="bulk-delete-btn"
                >
                  {bulkDeleting ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  ) : (
                    <Trash2 size={16} />
                  )}
                  <span>Excluir ({selectedImageIds.length})</span>
                </button>
                <button
                  onClick={toggleSelectionMode}
                  className="flex items-center gap-2 px-3 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg transition-colors"
                  data-testid="cancel-selection-btn"
                >
                  <X size={16} />
                  <span className="hidden sm:inline">Cancelar</span>
                </button>
              </>
            )}
            <button
              onClick={handleExportCSV}
              disabled={exporting || images.length === 0}
              className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
              data-testid="export-csv-btn"
            >
              <Download size={16} />
              <span className="hidden sm:inline">Exportar CSV</span>
            </button>
            <button
              onClick={() => {
                setImportFile(null);
                setImportResult(null);
                setShowImportModal(true);
              }}
              className="flex items-center gap-2 px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
              data-testid="import-csv-btn"
            >
              <FileSpreadsheet size={16} />
              <span className="hidden sm:inline">Importar CSV</span>
            </button>
            <button
              onClick={() => {
                setFormData({ name: '', description: '', category: '', image_url: '' });
                setEditingImage(null);
                setShowAddModal(true);
              }}
              className="flex items-center gap-2 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
              data-testid="add-gallery-image-btn"
            >
              <Plus size={16} />
              <span className="hidden sm:inline">Nova Imagem</span>
            </button>
          </div>
        )}
        {activeTab === 'import' && selectedProducts.length > 0 && (
          <button
            onClick={handleImportSelected}
            disabled={importingProducts}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            {importingProducts ? (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
            ) : (
              <Download size={16} />
            )}
            Importar {selectedProducts.length} Selecionados
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-700 pb-2">
        <button
          onClick={() => setActiveTab('gallery')}
          className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
            activeTab === 'gallery'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-800/50 text-gray-400 hover:text-white'
          }`}
        >
          📷 Galeria ({images.length})
        </button>
        <button
          onClick={() => setActiveTab('import')}
          className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
            activeTab === 'import'
              ? 'bg-green-600 text-white'
              : 'bg-gray-800/50 text-gray-400 hover:text-white'
          }`}
        >
          🏪 Importar de Lojas
        </button>
      </div>

      {/* Conteúdo baseado na aba ativa */}
      {activeTab === 'gallery' ? (
        <>
          {/* Filtros da Galeria */}
          <div className="glass-card p-4">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar por nome ou tags..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500"
                  />
                </div>
          </div>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white"
          >
            <option value="">Todas categorias</option>
            {predefinedCategories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Grid de Imagens */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      ) : images.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <ImageIcon size={64} className="mx-auto mb-4 text-gray-600" />
          <h3 className="text-xl text-gray-400 mb-2">Nenhuma imagem na galeria</h3>
          <p className="text-gray-500 text-sm">Clique em &quot;Nova Imagem&quot; para adicionar</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {images.map(image => {
            const isSelected = selectedImageIds.includes(image.id);
            return (
            <div
              key={image.id}
              onClick={selectionMode ? () => toggleSelectImage(image.id) : undefined}
              className={`glass-card overflow-hidden group relative transition-all ${selectionMode ? 'cursor-pointer' : ''} ${isSelected ? 'ring-2 ring-red-500' : ''}`}
            >
              {selectionMode && (
                <div className={`absolute top-2 left-2 z-10 w-6 h-6 rounded-md flex items-center justify-center border-2 ${isSelected ? 'bg-red-600 border-red-600' : 'bg-black/50 border-white/70'}`}>
                  {isSelected && <Check size={16} className="text-white" />}
                </div>
              )}
              <div className="aspect-square relative">
                <img
                  src={image.image_url}
                  alt={image.name}
                  className="w-full h-full object-cover"
                />
                {!selectionMode && (
                <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button
                    onClick={() => openEditModal(image)}
                    className="p-2 bg-blue-600 rounded-full hover:bg-blue-700"
                  >
                    <Edit size={16} className="text-white" />
                  </button>
                  <button
                    onClick={() => handleDelete(image.id)}
                    className="p-2 bg-red-600 rounded-full hover:bg-red-700"
                  >
                    <Trash2 size={16} className="text-white" />
                  </button>
                </div>
                )}
              </div>
              <div className="p-3">
                <h3 className="text-white text-sm font-medium truncate">{image.name}</h3>
                <p className="text-gray-500 text-xs mt-1">{image.category}</p>
                <div className="flex flex-wrap gap-1 mt-2">
                  {image.tags?.slice(0, 3).map((tag, i) => (
                    <span key={i} className="text-[10px] px-1.5 py-0.5 bg-gray-700 text-gray-300 rounded">
                      {tag}
                    </span>
                  ))}
                  {image.tags?.length > 3 && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-gray-700 text-gray-400 rounded">
                      +{image.tags.length - 3}
                    </span>
                  )}
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}
        </>
      ) : (
        /* ====== ABA IMPORTAR DE LOJAS ====== */
        <>
          {/* Info Box */}
          <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
            <p className="text-green-300 text-sm">
              <strong>💡 Dica:</strong> Selecione imagens de produtos das lojas para adicionar à galeria global. 
              O nome e categoria serão usados como palavras-chave automáticas.
            </p>
          </div>

          {/* Filtros de Importação */}
          <div className="glass-card p-4">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar produto..."
                    value={importFilters.search}
                    onChange={(e) => setImportFilters(prev => ({ ...prev, search: e.target.value }))}
                    className="w-full pl-10 pr-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500"
                  />
                </div>
              </div>
              <select
                value={importFilters.restaurant_id}
                onChange={(e) => setImportFilters(prev => ({ ...prev, restaurant_id: e.target.value }))}
                className="px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white min-w-[180px]"
              >
                <option value="">Todas as lojas</option>
                {restaurantsList.map(r => (
                  <option key={r.id} value={r.id}>
                    {r.name} ({r.product_count})
                  </option>
                ))}
              </select>
              <select
                value={importFilters.category}
                onChange={(e) => setImportFilters(prev => ({ ...prev, category: e.target.value }))}
                className="px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white min-w-[150px]"
              >
                <option value="">Todas categorias</option>
                {productCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Lista de Produtos */}
          {loadingProducts ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
            </div>
          ) : productsWithImages.length === 0 ? (
            <div className="glass-card p-12 text-center">
              <Store size={64} className="mx-auto mb-4 text-gray-600" />
              <h3 className="text-xl text-gray-400 mb-2">Nenhum produto com imagem encontrado</h3>
              <p className="text-gray-500 text-sm">Tente ajustar os filtros ou aguarde os lojistas adicionarem imagens</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {productsWithImages.map(product => {
                  const isSelected = selectedProducts.some(p => p.product_id === product.id);
                  return (
                    <div 
                      key={product.id} 
                      className={`glass-card overflow-hidden transition-all ${
                        product.already_in_gallery ? 'opacity-50' : ''
                      } ${isSelected ? 'ring-2 ring-green-500' : ''}`}
                    >
                      <div className="flex gap-4 p-4">
                        {/* Imagem */}
                        <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden bg-gray-800">
                          <img
                            src={product.image_url}
                            alt={product.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <h3 className="text-white font-medium truncate">{product.name}</h3>
                          <p className="text-gray-500 text-sm truncate">{product.restaurant_name}</p>
                          <p className="text-gray-400 text-xs mt-1">{product.category || 'Sem categoria'}</p>
                          {product.already_in_gallery && (
                            <span className="inline-block mt-2 px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded">
                              ✓ Já na galeria
                            </span>
                          )}
                        </div>
                      </div>
                      {/* Ações */}
                      {!product.already_in_gallery && (
                        <div className="flex gap-2 p-3 pt-0">
                          <button
                            onClick={() => toggleProductSelection(product)}
                            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              isSelected
                                ? 'bg-green-600 text-white'
                                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            }`}
                          >
                            {isSelected ? '✓ Selecionado' : 'Selecionar'}
                          </button>
                          <button
                            onClick={() => openImportProductModal(product)}
                            className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                          >
                            Importar
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Paginação */}
              {importTotalPages > 1 && (
                <div className="flex justify-center gap-2 mt-6">
                  <button
                    onClick={() => fetchProductsWithImages(importPage - 1)}
                    disabled={importPage === 1}
                    className="px-4 py-2 bg-gray-700 text-white rounded-lg disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <span className="px-4 py-2 text-gray-400">
                    Página {importPage} de {importTotalPages}
                  </span>
                  <button
                    onClick={() => fetchProductsWithImages(importPage + 1)}
                    disabled={importPage === importTotalPages}
                    className="px-4 py-2 bg-gray-700 text-white rounded-lg disabled:opacity-50"
                  >
                    Próxima
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Modal Importar Produto Individual */}
      <Dialog open={showImportProductModal} onOpenChange={setShowImportProductModal}>
        <DialogContent className="bg-gray-900 border border-gray-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">Importar para Galeria</DialogTitle>
          </DialogHeader>
          
          {productToImport && (
            <div className="space-y-4 py-4">
              {/* Preview da imagem */}
              <div className="flex gap-4 items-start">
                <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-800 flex-shrink-0">
                  <img
                    src={productToImport.image_url}
                    alt={productToImport.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Produto original:</p>
                  <p className="text-white font-medium">{productToImport.name}</p>
                  <p className="text-gray-500 text-xs">{productToImport.restaurant_name}</p>
                </div>
              </div>

              {/* Formulário */}
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Nome na Galeria</label>
                  <input
                    type="text"
                    value={importFormData.name}
                    onChange={(e) => setImportFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Palavras-chave (separadas por vírgula)</label>
                  <input
                    type="text"
                    value={importFormData.keywords}
                    onChange={(e) => setImportFormData(prev => ({ ...prev, keywords: e.target.value }))}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                    placeholder="hamburguer, lanche, bacon..."
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Categoria</label>
                  <select
                    value={importFormData.category}
                    onChange={(e) => setImportFormData(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                  >
                    {predefinedCategories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Botões */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowImportProductModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleImportProduct}
                  disabled={importingProducts || !importFormData.name}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                >
                  {importingProducts ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  ) : (
                    <>
                      <Download size={16} />
                      Importar
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Adicionar/Editar */}
      <Dialog open={showAddModal || showEditModal} onOpenChange={(open) => {
        if (!open) {
          setShowAddModal(false);
          setShowEditModal(false);
          setEditingImage(null);
          setNewCategory('');
          setShowNewCategoryInput(false);
        }
      }}>
        <DialogContent className="bg-gray-900 border border-gray-800 max-w-md max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-white text-xl">
              {editingImage ? 'Editar Imagem' : 'Nova Imagem'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto space-y-4 pr-1" style={{ WebkitOverflowScrolling: 'touch' }}>
            {/* Área de Upload/Preview */}
            <div className="space-y-3">
              <label className="block text-sm text-gray-300 mb-1">Imagem *</label>
              
              {/* Preview da imagem (upload ou URL) */}
              {(imagePreview || formData.image_url) && (
                <div className="relative aspect-video rounded-lg overflow-hidden bg-gray-800">
                  <img
                    src={imagePreview || formData.image_url}
                    alt="Preview"
                    className="w-full h-full object-cover"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                  {imagePreview && (
                    <button
                      type="button"
                      onClick={clearImagePreview}
                      className="absolute top-2 right-2 p-1 bg-red-600 rounded-full hover:bg-red-700"
                    >
                      <XCircle size={16} className="text-white" />
                    </button>
                  )}
                </div>
              )}
              
              {/* Área de upload */}
              {!imagePreview && !formData.image_url && (
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-600 rounded-lg p-6 text-center cursor-pointer hover:border-gray-500 transition-colors"
                >
                  <Upload size={28} className="mx-auto mb-2 text-gray-500" />
                  <p className="text-gray-400 text-sm">Clique para fazer upload</p>
                  <p className="text-gray-500 text-xs mt-1">JPG, PNG ou WEBP (máx. 5MB)</p>
                </div>
              )}
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/jpg"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              {/* Botão de upload se arquivo selecionado */}
              {selectedFile && !uploading && (
                <button
                  type="button"
                  onClick={handleUpload}
                  className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <Upload size={16} />
                  Enviar Imagem
                </button>
              )}
              
              {uploading && (
                <div className="flex items-center justify-center gap-2 py-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent"></div>
                  <span className="text-gray-400">Enviando...</span>
                </div>
              )}
              
              {/* Divider */}
              <div className="relative py-2">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-700"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-2 bg-gray-900 text-gray-500">ou cole uma URL</span>
                </div>
              </div>
              
              {/* URL Input */}
              <input
                type="url"
                value={formData.image_url}
                onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                placeholder="https://..."
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-300 mb-1">Nome *</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Ex: X-Bacon Artesanal"
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-300 mb-1">Categoria *</label>
              {!showNewCategoryInput ? (
                <div className="flex gap-2">
                  <select
                    required={!newCategory}
                    value={formData.category}
                    onChange={(e) => {
                      if (e.target.value === '__new__') {
                        setShowNewCategoryInput(true);
                        setFormData({...formData, category: ''});
                      } else {
                        setFormData({...formData, category: e.target.value});
                      }
                    }}
                    className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                  >
                    <option value="">Selecione...</option>
                    {predefinedCategories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                    <option value="__new__" className="text-green-400">+ Nova categoria</option>
                  </select>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="text"
                    required
                    value={newCategory}
                    onChange={(e) => {
                      setNewCategory(e.target.value);
                      setFormData({...formData, category: e.target.value.toLowerCase().trim()});
                    }}
                    placeholder="Nome da nova categoria"
                    className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setShowNewCategoryInput(false);
                      setNewCategory('');
                      setFormData({...formData, category: ''});
                    }}
                    className="px-3 py-2 text-gray-400 hover:text-white border border-gray-700 rounded-lg"
                  >
                    <X size={16} />
                  </button>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm text-gray-300 mb-1">
                Descrição (palavras-chave) *
              </label>
              <textarea
                required
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="hamburguer bacon cheddar alface tomate artesanal gourmet..."
                rows={2}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white resize-none"
              />
              <p className="text-xs text-gray-500 mt-1">
                Separe as palavras por espaço. Serão usadas para vincular automaticamente aos produtos.
              </p>
            </div>

            {/* Botões de ação - Fixos no final */}
            <div className="flex gap-3 pt-2 pb-2 sticky bottom-0 bg-gray-900">
              <button
                type="button"
                onClick={() => {
                  setShowAddModal(false);
                  setShowEditModal(false);
                }}
                className="flex-1 px-4 py-3 text-gray-400 hover:text-white border border-gray-700 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 font-medium"
              >
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal de Importação CSV */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="bg-gray-900 border border-gray-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white text-xl flex items-center gap-2">
              <FileSpreadsheet size={22} className="text-purple-400" />
              Importar Imagens via CSV
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
              <p className="text-sm text-purple-300">
                <strong>Importante:</strong> Apenas URLs do Cloudinary são aceitas. Use este recurso para reciclar imagens já existentes no Cloudinary.
              </p>
            </div>

            {/* Botão Download Template */}
            <button
              onClick={downloadTemplate}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg border border-gray-700 transition-colors"
            >
              <Download size={16} />
              Baixar Template CSV
            </button>

            {/* Input de arquivo */}
            <div>
              <label className="block text-sm text-gray-300 mb-2">Arquivo CSV</label>
              <input
                ref={csvInputRef}
                type="file"
                accept=".csv"
                onChange={(e) => setImportFile(e.target.files[0])}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white file:mr-4 file:py-1 file:px-3 file:rounded file:border-0 file:bg-purple-600 file:text-white file:cursor-pointer"
              />
            </div>

            {/* Resultado da importação */}
            {importResult && (
              <div className={`p-3 rounded-lg ${importResult.imported > 0 ? 'bg-green-500/10 border border-green-500/30' : 'bg-yellow-500/10 border border-yellow-500/30'}`}>
                <p className={`font-medium ${importResult.imported > 0 ? 'text-green-400' : 'text-yellow-400'}`}>
                  {importResult.message}
                </p>
                {importResult.errors && importResult.errors.length > 0 && (
                  <div className="mt-2 max-h-32 overflow-y-auto">
                    <p className="text-xs text-gray-400 mb-1">Erros encontrados:</p>
                    {importResult.errors.map((err, i) => (
                      <p key={i} className="text-xs text-red-400">{err}</p>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setShowImportModal(false)}
                className="flex-1 px-4 py-2 text-gray-400 hover:text-white border border-gray-700 rounded-lg transition-colors"
              >
                Fechar
              </button>
              <button
                onClick={handleImportCSV}
                disabled={!importFile || importing}
                className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {importing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Importando...
                  </>
                ) : (
                  <>
                    <Upload size={16} />
                    Importar
                  </>
                )}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ============================================================
// GERENCIAMENTO UAZAPI - MASTER
// Vincular instâncias UazAPI a lojas individuais
// ============================================================

